import { useState, useEffect } from 'react';
import { collection, onSnapshot, query, orderBy, doc, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { AY_ADLARI, ayKey, paraCevir } from '../utils/takvim';
import { 
  ChevronLeft, ChevronRight, TrendingUp, Users, 
  MapPin, Wallet, AlertCircle, HardHat, BarChart3,
  CreditCard, CheckCircle2, Send, Bell, User, MessageSquare,
  Clock, Trash2, Loader2
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

export default function Dashboard() {
  const bugun = new Date();
  const [yil, setYil] = useState(bugun.getFullYear());
  const [ay, setAy] = useState(bugun.getMonth());
  const [ustalar, setUstalar] = useState([]);
  const [santiyeler, setSantiyeler] = useState([]);
  const [calisma, setCalisma] = useState({}); 
  const [avanslar, setAvanslar] = useState({});
  const [odemeler, setOdemeler] = useState([]);
  const [kasaVerisi, setKasaVerisi] = useState([]);

  // Bildirim states
  const [mod, setMod] = useState('tek');
  const [ustaId, setUstaId] = useState('');
  const [baslik, setBaslik] = useState('');
  const [mesaj, setMesaj] = useState('');
  const [yukleniyor, setYukleniyor] = useState(false);
  const [gonderildi, setGonderildi] = useState(false);
  const [hata, setHata] = useState('');
  const [gonderilenler, setGonderilenler] = useState([]);
  const [gecmisAcik, setGecmisAcik] = useState(false);

  function ayDegistir(delta) {
    let yeniAy = ay + delta;
    let yeniYil = yil;
    if (yeniAy > 11) { yeniAy = 0; yeniYil++; }
    if (yeniAy < 0) { yeniAy = 11; yeniYil--; }
    setAy(yeniAy); 
    setYil(yeniYil);
  }

  useEffect(() => {
    const q = query(collection(db, 'ustalar'), orderBy('olusturmaTarihi', 'desc'));
    const unsub = onSnapshot(q, snap => {
      const tumUstalar = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const sadeceAktifler = tumUstalar.filter(u => u.aktif === true);
      setUstalar(sadeceAktifler);
    });
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'santiyeler'), snap =>
      setSantiyeler(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return unsub;
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'kasa'), orderBy('tarih', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      const veriler = snap.docs.map(d => ({ ...d.data(), tarih: d.data().tarih.toDate() }));
      let akumuleBakiye = 0;
      const grafikData = Array.from({ length: 31 }, (_, i) => {
        const gun = i + 1;
        const oGunkuIslemler = veriler.filter(v => 
          v.tarih.getDate() === gun && 
          v.tarih.getMonth() === ay && 
          v.tarih.getFullYear() === yil
        );
        oGunkuIslemler.forEach(islem => {
          if (islem.tur === 'gelir') akumuleBakiye += islem.tutar;
          else akumuleBakiye -= islem.tutar;
        });
        return { gun, bakiye: akumuleBakiye };
      });
      setKasaVerisi(grafikData);
    });
    return unsub;
  }, [yil, ay]);

  useEffect(() => {
    const ak = ayKey(yil, ay);
    const unsub1 = onSnapshot(doc(db, 'aylikCalisma', ak), snap => setCalisma(snap.exists() ? snap.data().kayitlar : {}));
    const unsub2 = onSnapshot(doc(db, 'aylikAvans', ak), snap => setAvanslar(snap.exists() ? snap.data() : {}));
    return () => { unsub1(); unsub2(); };
  }, [yil, ay]);

  useEffect(() => {
    const ak = ayKey(yil, ay);
    const unsub = onSnapshot(
      query(collection(db, 'odemeler'), orderBy('tarih', 'desc')),
      snap => {
        const tumOdemeler = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const aylikOdemeler = tumOdemeler.filter(o => {
          if (o.ilgiliAy) return o.ilgiliAy === ak;
          const tarih = o.tarih?.toDate ? o.tarih.toDate() : new Date(o.tarih);
          return ayKey(tarih.getFullYear(), tarih.getMonth()) === ak;
        });
        setOdemeler(aylikOdemeler);
      }
    );
    return unsub;
  }, [yil, ay]);

  // Bildirimleri dinle
  useEffect(() => {
    const q = query(collection(db, 'bildirimler'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const liste = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setGonderilenler(liste);
    });
    return () => unsub();
  }, []);

  const hesapla = (uId, yovmiye) => {
    let gun = 0;
    const lok = new Set();
    Object.entries(calisma).forEach(([_, uMap]) => {
      if (uMap[uId]) { 
        gun++; 
        const s = santiyeler.find(x => x.id === uMap[uId]); 
        if (s) lok.add(s.ad); 
      }
    });

    let av = 0;
    const avansVerisi = avanslar[uId];
    if (avansVerisi) {
      if (typeof avansVerisi === 'number') {
        av = avansVerisi;
      } else if (typeof avansVerisi === 'object') {
        av = Object.values(avansVerisi).reduce((a, b) => a + Number(b || 0), 0);
      }
    }

    let odeme = 0;
    odemeler.filter(o => o.ustaId === uId).forEach(o => {
      odeme += Number(o.tutar || 0);
    });

    const brut = gun * Number(yovmiye || 0);
    const net = brut - av - odeme;

    return { gun, lok: [...lok], av, odeme, brut, net };
  };

  const ozet = ustalar.map(u => ({ ...u, ...hesapla(u.id, u.yovmiye) }));

  const santiyeDagilimi = {};
  Object.values(calisma).forEach(uMap => {
    Object.values(uMap).forEach(sId => {
      santiyeDagilimi[sId] = (santiyeDagilimi[sId] || 0) + 1;
    });
  });

  const toplamBrut = ozet.reduce((t, u) => t + u.brut, 0);
  const toplamAvans = ozet.reduce((t, u) => t + u.av, 0);
  const toplamOdeme = ozet.reduce((t, u) => t + u.odeme, 0);
  const toplamNet = ozet.reduce((t, u) => t + u.net, 0);
  const toplamGun = ozet.reduce((t, u) => t + u.gun, 0);

  // Bildirim gönder
  const handleGonder = async (e) => {
    e.preventDefault();
    if (!baslik.trim() || !mesaj.trim()) return;
    if (mod === 'tek' && !ustaId) {
      setHata('Lütfen bir usta seçin');
      return;
    }

    setYukleniyor(true);
    setHata('');

    try {
      if (mod === 'tek') {
        await addDoc(collection(db, 'bildirimler'), {
          ustaId,
          title: baslik,
          body: mesaj,
          data: { type: 'admin_notification', url: '/panel' },
          status: 'pending',
          shown: false,
          createdAt: serverTimestamp(),
          createdBy: 'admin'
        });
      } else {
        await addDoc(collection(db, 'bildirimler'), {
          target: 'all_ustalar',
          title: baslik,
          body: mesaj,
          data: { type: 'admin_broadcast', url: '/panel' },
          status: 'pending',
          shown: false,
          createdAt: serverTimestamp(),
          createdBy: 'admin'
        });
      }

      setGonderildi(true);
      setBaslik('');
      setMesaj('');
      setUstaId('');
      setTimeout(() => setGonderildi(false), 3000);
    } catch (err) {
      setHata('Gönderilemedi: ' + err.message);
    } finally {
      setYukleniyor(false);
    }
  };

  const silBildirim = async (id) => {
    if (!confirm('Bu bildirimi silmek istediğinize emin misiniz?')) return;
    try {
      await updateDoc(doc(db, 'bildirimler', id), { status: 'deleted' });
    } catch (err) {
      console.error('Silme hatası:', err);
    }
  };

  const formatTarih = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('tr-TR', { 
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' 
    });
  };

  const aktifBildirimler = gonderilenler.filter(b => b.status !== 'deleted');
  const bekleyenSayisi = aktifBildirimler.filter(b => b.status === 'pending').length;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 pb-24">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-white tracking-tight">Dashboard</h1>
          <p className="text-xs text-stone-500 font-bold uppercase tracking-widest">Genel Özet ve Hakedişler</p>
        </div>
        <div className="flex items-center bg-stone-900 border border-stone-800 rounded-xl p-1">
          <button onClick={() => ayDegistir(-1)} className="p-2 text-stone-400 hover:bg-stone-800 rounded-lg transition-colors"><ChevronLeft size={18}/></button>
          <span className="text-xs font-black text-white px-4 min-w-[120px] text-center uppercase">{AY_ADLARI[ay]} {yil}</span>
          <button onClick={() => ayDegistir(1)} className="p-2 text-stone-400 hover:bg-stone-800 rounded-lg transition-colors"><ChevronRight size={18}/></button>
        </div>
      </div>

      {/* Stat Kartları */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard label="Brüt Hakediş" val={paraCevir(toplamBrut)} icon={Wallet} color="text-stone-300" bg="bg-stone-900" />
        <StatCard label="Toplam Avans" val={paraCevir(toplamAvans)} icon={AlertCircle} color="text-yellow-500" bg="bg-yellow-500/5" />
        <StatCard label="Yapılan Ödeme" val={paraCevir(toplamOdeme)} icon={CreditCard} color="text-blue-400" bg="bg-blue-400/5" />
        <StatCard label="Net Ödenecek" val={paraCevir(toplamNet)} icon={TrendingUp} color="text-green-400" bg="bg-green-400/5" />
        <StatCard label="Toplam Mesai" val={`${toplamGun} Gün`} icon={Users} color="text-blue-400" bg="bg-blue-400/5" />
      </div>

      {/* Şantiye Dağılımı */}
      <div className="bg-stone-900 border border-stone-800 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4 text-stone-400">
          <MapPin size={16}/> <h2 className="text-xs font-bold uppercase tracking-widest">Şantiye Bazlı Çalışma</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(santiyeDagilimi).map(([sId, gun]) => {
            const s = santiyeler.find(x => x.id === sId);
            if (!s) return null;
            return (
              <div key={sId} className="flex items-center gap-3 bg-stone-800/40 border border-stone-700/50 rounded-xl px-4 py-2.5">
                <span className="text-sm font-bold text-stone-200">{s.ad}</span>
                <span className="text-[10px] font-black bg-stone-700 text-stone-400 px-2 py-0.5 rounded-full">{gun} GÜN</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Usta Hakediş Detayları */}
      <div className="bg-stone-900 border border-stone-800 rounded-3xl overflow-hidden shadow-2xl">
        <div className="px-6 py-4 border-b border-stone-800 bg-stone-900/50 flex items-center gap-2">
          <HardHat size={16} className="text-stone-500"/>
          <h2 className="text-xs font-bold text-white uppercase tracking-widest">Usta Hakediş Detayları</h2>
        </div>
        <div className="divide-y divide-stone-800/50">
          {ozet.map(u => {
            const odendiMi = u.net <= 0 && u.brut > 0;
            const borcluMu = u.net > 0;

            return (
              <div key={u.id} className="p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 hover:bg-stone-800/20 transition-all">
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-12 h-12 rounded-2xl bg-stone-800 border border-stone-700 flex items-center justify-center text-stone-500 font-black text-lg relative">
                    {u.ad[0].toUpperCase()}
                    {odendiMi && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                        <CheckCircle2 size={12} className="text-white" />
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold text-white">{u.ad}</h3>
                      {odendiMi && (
                        <span className="text-[9px] font-black bg-green-500/10 text-green-400 px-2 py-0.5 rounded-full border border-green-500/20">ÖDENDİ ✓</span>
                      )}
                      {borcluMu && (
                        <span className="text-[9px] font-black bg-red-500/10 text-red-400 px-2 py-0.5 rounded-full border border-red-500/20">BORÇLU</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {u.lok.map(l => (
                        <span key={l} className="text-[9px] font-bold bg-stone-800 text-stone-500 px-1.5 py-0.5 rounded border border-stone-700/30">{l}</span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-4 sm:flex items-center gap-4 sm:gap-6 border-t border-stone-800 sm:border-0 pt-4 sm:pt-0">
                  <DataBox label="Gün" val={u.gun} />
                  <DataBox label="Avans" val={paraCevir(u.av)} color="text-yellow-600" />
                  <DataBox label="Ödeme" val={paraCevir(u.odeme)} color="text-blue-400" />
                  <DataBox label="Net" val={paraCevir(u.net)} color={u.net > 0 ? 'text-red-400' : u.net < 0 ? 'text-yellow-400' : 'text-green-400'} bold />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* BİLDİRİM GÖNDER KARTI */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <div className="bg-stone-900 border border-stone-800 rounded-3xl overflow-hidden shadow-2xl">
        <div className="px-6 py-4 border-b border-stone-800 bg-stone-900/50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
              <Bell size={20} className="text-emerald-400" />
            </div>
            <div>
              <h2 className="text-sm font-black text-white uppercase tracking-widest">Bildirim Gönder</h2>
              <p className="text-[10px] text-stone-500 font-bold">Usta paneline anlık bildirim</p>
            </div>
          </div>
          {bekleyenSayisi > 0 && (
            <span className="px-3 py-1 bg-emerald-500/10 text-emerald-400 text-[10px] font-black rounded-full border border-emerald-500/20">
              {bekleyenSayisi} BEKLEYEN
            </span>
          )}
        </div>

        <form onSubmit={handleGonder} className="p-6 space-y-5">
          {/* Mod Seçimi */}
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => { setMod('tek'); setUstaId(''); setHata(''); }}
              className={`py-3 px-4 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2
                ${mod === 'tek' 
                  ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' 
                  : 'bg-stone-800 text-stone-500 border border-stone-700 hover:border-stone-600'}`}
            >
              <User size={14} /> Tek Usta
            </button>
            <button
              type="button"
              onClick={() => { setMod('toplu'); setUstaId(''); setHata(''); }}
              className={`py-3 px-4 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2
                ${mod === 'toplu' 
                  ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' 
                  : 'bg-stone-800 text-stone-500 border border-stone-700 hover:border-stone-600'}`}
            >
              <Users size={14} /> Tüm Ustalar
            </button>
          </div>

          {/* Usta Seçimi */}
          {mod === 'tek' && (
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-stone-500 uppercase tracking-widest ml-1">Usta Seçin</label>
              <select
                value={ustaId}
                onChange={e => { setUstaId(e.target.value); setHata(''); }}
                className="w-full bg-stone-800 border border-stone-700 rounded-xl p-3 text-sm text-white font-bold outline-none focus:border-emerald-500 appearance-none"
              >
                <option value="">Usta seçin...</option>
                {ustalar.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.ad} {u.soyad || ''} {u.kod ? `(Kod: ${u.kod})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Başlık */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-stone-500 uppercase tracking-widest ml-1">Bildirim Başlığı</label>
            <input
              type="text"
              value={baslik}
              onChange={e => setBaslik(e.target.value)}
              placeholder="Örn: Yeni Görev Atandı"
              className="w-full bg-stone-800 border border-stone-700 rounded-xl p-3 text-sm text-white font-bold outline-none focus:border-emerald-500 placeholder-stone-600"
              required
            />
          </div>

          {/* Mesaj */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-stone-500 uppercase tracking-widest ml-1">Mesaj İçeriği</label>
            <textarea
              value={mesaj}
              onChange={e => setMesaj(e.target.value)}
              placeholder="Ustaya iletilecek mesaj..."
              rows={3}
              className="w-full bg-stone-800 border border-stone-700 rounded-xl p-3 text-sm text-white font-bold outline-none focus:border-emerald-500 resize-none placeholder-stone-600"
              required
            />
          </div>

          {/* Hata / Başarı */}
          {hata && (
            <div className="flex items-center gap-2 text-red-400 text-xs bg-red-400/5 border border-red-400/10 rounded-xl px-4 py-3">
              <AlertCircle size={14} /> {hata}
            </div>
          )}
          {gonderildi && (
            <div className="flex items-center gap-2 text-emerald-400 text-xs bg-emerald-400/5 border border-emerald-400/10 rounded-xl px-4 py-3">
              <CheckCircle2 size={14} /> Bildirim başarıyla gönderildi!
            </div>
          )}

          {/* Gönder Butonu */}
          <button
            type="submit"
            disabled={yukleniyor || !baslik.trim() || !mesaj.trim()}
            className="w-full bg-emerald-500 text-stone-950 font-black py-4 rounded-2xl text-sm uppercase tracking-widest hover:bg-emerald-400 active:scale-[0.98] transition-all disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {yukleniyor ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            {yukleniyor ? 'GÖNDERİLİYOR...' : 'BİLDİRİM GÖNDER'}
          </button>
        </form>
      </div>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* GÖNDERİLEN BİLDİRİMLER GEÇMİŞİ */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {aktifBildirimler.length > 0 && (
        <div className="bg-stone-900 border border-stone-800 rounded-3xl overflow-hidden shadow-2xl">
          <button
            onClick={() => setGecmisAcik(!gecmisAcik)}
            className="w-full px-6 py-4 border-b border-stone-800 bg-stone-900/50 flex items-center justify-between hover:bg-stone-800/30 transition-colors"
          >
            <div className="flex items-center gap-3">
              <MessageSquare size={16} className="text-stone-500" />
              <h2 className="text-xs font-black text-white uppercase tracking-widest">Gönderilen Bildirimler</h2>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[10px] text-stone-600 font-bold">{aktifBildirimler.length} bildirim</span>
              {gecmisAcik ? <ChevronUp size={18} className="text-stone-500" /> : <ChevronDown size={18} className="text-stone-500" />}
            </div>
          </button>

          {gecmisAcik && (
            <div className="divide-y divide-stone-800/50 max-h-96 overflow-y-auto">
              {aktifBildirimler.map(b => {
                const usta = ustalar.find(u => u.id === b.ustaId);
                const isPending = b.status === 'pending';
                const isBroadcast = b.target === 'all_ustalar';

                return (
                  <div key={b.id} className="px-6 py-4 hover:bg-stone-800/20 transition-all">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-[9px] font-black px-2 py-0.5 rounded-full border ${
                            isBroadcast 
                              ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' 
                              : 'bg-purple-500/10 text-purple-400 border-purple-500/20'
                          }`}>
                            {isBroadcast ? 'TÜM USTALAR' : 'TEK USTA'}
                          </span>
                          <span className={`text-[9px] font-black px-2 py-0.5 rounded-full border ${
                            isPending 
                              ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' 
                              : 'bg-green-500/10 text-green-400 border-green-500/20'
                          }`}>
                            {isPending ? 'BEKLİYOR' : 'OKUNDU'}
                          </span>
                        </div>
                        <p className="text-sm font-bold text-white">{b.title}</p>
                        <p className="text-xs text-stone-400 mt-1">{b.body}</p>
                        <div className="flex items-center gap-3 mt-2">
                          <span className="text-[10px] text-stone-600 font-bold flex items-center gap-1">
                            <Clock size={10} /> {formatTarih(b.createdAt)}
                          </span>
                          {!isBroadcast && usta && (
                            <span className="text-[10px] text-stone-600 font-bold">→ {usta.ad} {usta.soyad || ''}</span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => silBildirim(b.id)}
                        className="p-2 text-stone-600 hover:text-red-400 hover:bg-stone-800 rounded-xl transition-all flex-shrink-0"
                        title="Sil"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, val, icon: Icon, color, bg }) {
  return (
    <div className={`${bg} border border-stone-800 p-5 rounded-3xl shadow-lg`}>
      <div className="flex items-center gap-2 mb-3 text-stone-500">
        <Icon size={14}/> <span className="text-[10px] font-black uppercase tracking-wider">{label}</span>
      </div>
      <div className={`text-xl font-black tracking-tight ${color}`}>{val}</div>
    </div>
  );
}

function DataBox({ label, val, color="text-stone-300", bold=false }) {
  return (
    <div className="text-center sm:text-right">
      <div className="text-[9px] text-stone-600 uppercase font-black mb-0.5">{label}</div>
      <div className={`text-xs ${bold ? 'font-black' : 'font-bold'} ${color} truncate`}>{val}</div>
    </div>
  );
}